/**
 * LocalTrackRecovery (F1) — watchdog for the publisher's own camera + mic.
 * livekit-client tries ONE restartTrack() when a track ends; if that fails it
 * mutes the track for good (black tile). Frozen tracks (MediaStreamTrack.muted
 * while the user did NOT mute) are never handled. This component retries with
 * backoff and tells the user when recovery succeeds. Server-side grants are
 * untouched — we only re-acquire the device for an already-published source.
 */
import { useEffect } from "react";
import { useRoomContext } from "@livekit/components-react";
import { RoomEvent, Track, TrackEvent } from "livekit-client";
import { toast } from "sonner";

const TICK_MS = 2000;
const FROZEN_MS = 4000;
const BACKOFF_MS = [1500, 3000, 6000, 10000, 15000];
const LABEL = { [Track.Source.Camera]: "Camera", [Track.Source.Microphone]: "Microphone" };

export default function LocalTrackRecovery({ enabled = true, streamId }) {
  const room = useRoomContext();

  useEffect(() => {
    if (!room || !enabled) return undefined;
    const lp = room.localParticipant;
    if (process.env.NODE_ENV !== "production") window.__pandaxRoom = room; // Preview test hook only
    const fresh = () => ({ frozenSince: 0, attempts: 0, busy: false, nextAt: 0, endedUnmuted: false });
    const state = { [Track.Source.Camera]: fresh(), [Track.Source.Microphone]: fresh() };
    let alive = true;

    const healthy = (pub) => {
      const mst = pub?.track?.mediaStreamTrack;
      return !!mst && mst.readyState === "live" && !mst.muted;
    };

    const recover = async (source, pub) => {
      const s = state[source];
      if (s.busy || Date.now() < s.nextAt || s.attempts >= BACKOFF_MS.length) return;
      s.busy = true;
      const enable = source === Track.Source.Camera
        ? (on) => lp.setCameraEnabled(on) : (on) => lp.setMicrophoneEnabled(on);
      try {
        if (pub.isMuted) { await enable(true); }
        else { try { await pub.track.restartTrack(); } catch { await enable(false); await enable(true); } }
        await new Promise((r) => setTimeout(r, 1200));
        const now = lp.getTrackPublication(source);
        if (now && !now.isMuted && healthy(now)) {
          s.attempts = 0; s.frozenSince = 0; s.endedUnmuted = false;
          toast.success(`${LABEL[source]} reconnected.`, { id: `recover-${source}` });
          window.dispatchEvent(new CustomEvent("pandax:track-recovered", { detail: { source, streamId } }));
        } else {
          s.attempts += 1; s.nextAt = Date.now() + BACKOFF_MS[Math.min(s.attempts, BACKOFF_MS.length - 1)];
        }
      } catch {
        s.attempts += 1; s.nextAt = Date.now() + BACKOFF_MS[Math.min(s.attempts, BACKOFF_MS.length - 1)];
      } finally { s.busy = false; }
    };

    const tick = () => {
      if (!alive || room.state !== "connected" || document.visibilityState === "hidden") return;
      for (const source of [Track.Source.Camera, Track.Source.Microphone]) {
        const pub = lp.getTrackPublication(source);
        const s = state[source];
        if (!pub?.track) { s.frozenSince = 0; continue; }
        // SDK mutes a track it failed to restart — we only treat mute as user intent
        // when the track did not end while unmuted.
        if (pub.isMuted && !s.endedUnmuted) { s.frozenSince = 0; continue; }
        if (!pub.isMuted && healthy(pub)) { s.frozenSince = 0; s.endedUnmuted = false; if (s.attempts && !s.busy) s.attempts = 0; continue; }
        const mst = pub.track.mediaStreamTrack;
        if (pub.isMuted || mst?.readyState === "ended") { recover(source, pub); continue; }
        if (!s.frozenSince) s.frozenSince = Date.now();
        else if (Date.now() - s.frozenSince >= FROZEN_MS) recover(source, pub);
      }
    };
    const onEnded = (track) => {
      const src = track?.source;
      if (state[src] && !track.isMuted) state[src].endedUnmuted = true;
    };
    const watch = (pub) => { const tr = pub?.track; if (tr && state[tr.source]) tr.on(TrackEvent.Ended, () => onEnded(tr)); };
    lp.trackPublications.forEach(watch);
    const onPublished = (pub) => watch(pub);
    room.on(RoomEvent.LocalTrackPublished, onPublished);
    const t = setInterval(tick, TICK_MS);
    return () => { alive = false; clearInterval(t); room.off(RoomEvent.LocalTrackPublished, onPublished); };
  }, [room, enabled, streamId]);

  return null;
}
